import {
	BigNum,
	BN,
	JupiterApiVersion,
	LEGACY_JUPITER_API,
	SpotMarketConfig,
	SwapClientType,
	SwapMode,
	UnifiedQuoteResponse,
	UnifiedSwapClient,
	WRAPPED_SOL_MINT,
} from '@velocity-exchange/sdk';
import { Connection, PublicKey } from '@solana/web3.js';
import { formatText } from '@velocity-exchange/common/format';
import { parseInputBigNumOrZero } from 'src/utils/parseInputBigNum';

/**
 * Titan pays SOL out natively rather than crediting the wrapped-SOL token
 * account `endSwap` measures, so a swap into SOL always reverts on-chain with
 * `amount_out must be greater than 0`. Jupiter credits the account.
 */
export const swapOutputForcesJupiter = (outputMint: PublicKey): boolean =>
	outputMint.equals(WRAPPED_SOL_MINT);

/**
 * The provider a swap will actually run on. Displays and execution must both go
 * through this, or the UI names one provider while the swap uses another.
 */
export const getEffectiveSwapClientType = (
	configured: SwapClientType,
	outputMint: PublicKey
): SwapClientType =>
	swapOutputForcesJupiter(outputMint) ? 'jupiter' : configured;

/**
 * Jupiter v2 (`GET /swap/v2/build`) exists only on the keyed `api.jup.ag` host,
 * which `lite-api` — where v1 runs — answers with a 404. The proxy route holds
 * the key, so it isn't shipped to the browser.
 */
export const JUPITER_V2_PROXY_URL = '/api/jupiter/proxy';

export const createSwapClient = ({
	clientType,
	connection,
	jupiterApiVersion = 'v1',
}: {
	clientType: SwapClientType;
	connection: Connection;
	jupiterApiVersion?: JupiterApiVersion;
}) => {
	const isJupiter = clientType === 'jupiter';

	return new UnifiedSwapClient({
		clientType,
		connection,
		authToken: '',
		proxyUrl: clientType === 'titan' ? '/api/titan/proxy' : undefined,
		url: isJupiter
			? jupiterApiVersion === 'v2'
				? JUPITER_V2_PROXY_URL
				: LEGACY_JUPITER_API
			: undefined,
		jupiterApiVersion: isJupiter ? jupiterApiVersion : undefined,
	});
};

/**
 * Whether Jupiter's v2 build endpoint can price this request.
 *
 * `/swap/v2/build` is ExactIn-only, has no auto-slippage equivalent, builds
 * for a named `taker`, and ignores `onlyDirectRoutes` (it still returns
 * multi-hop routes). It rejects none of those itself — an ExactOut amount
 * comes back spent as the input, auto-slippage params are ignored for a quote
 * at zero slippage tolerance, and a missing taker is a 400 — so the SDK throws
 * instead, and those requests stay on v1.
 *
 * The taker matters most while disconnected: v1 prices a quote with no wallet,
 * which is what the forms show before a user connects.
 */
export const jupiterV2CanQuote = ({
	swapMode = 'ExactIn',
	autoSlippage = false,
	userPublicKey,
	onlyDirectRoutes = false,
}: {
	swapMode?: SwapMode;
	autoSlippage?: boolean;
	userPublicKey?: PublicKey | null;
	onlyDirectRoutes?: boolean;
}): boolean =>
	swapMode === 'ExactIn' &&
	!autoSlippage &&
	!!userPublicKey &&
	!onlyDirectRoutes;

/** One v2 client per connection — a fresh one drops its lookup-table cache. */
const jupiterV2Clients = new WeakMap<Connection, UnifiedSwapClient>();

/**
 * The client that should issue a quote request, which is not always the one
 * that will execute it.
 *
 * Only quoting is version-specific: a v2 quote carries the instructions that
 * execute it, and every Jupiter client builds from whichever payload a quote
 * holds, so callers can keep executing with the client they already have.
 * Titan and requests v2 can't price fall through unchanged.
 *
 * Pass the wallet that will sign, and request the quote with the same key: v2
 * binds the route to its taker, so one taken for another wallet is rejected at
 * build time. That is the connected wallet, never the active account's
 * `authority` — those differ for an account delegated to it.
 */
export const getQuoteSwapClient = <T extends UnifiedSwapClient | null>({
	swapClient,
	connection,
	swapMode,
	autoSlippage,
	userPublicKey,
	onlyDirectRoutes,
}: {
	swapClient: T;
	connection: Connection | null | undefined;
	swapMode?: SwapMode;
	autoSlippage?: boolean;
	userPublicKey?: PublicKey | null;
	onlyDirectRoutes?: boolean;
}): T | UnifiedSwapClient => {
	if (
		!swapClient?.isJupiter() ||
		!connection ||
		!jupiterV2CanQuote({
			swapMode,
			autoSlippage,
			userPublicKey,
			onlyDirectRoutes,
		})
	) {
		return swapClient;
	}

	const cached = jupiterV2Clients.get(connection);

	if (cached) {
		return cached;
	}

	const v2Client = createSwapClient({
		clientType: 'jupiter',
		connection,
		jupiterApiVersion: 'v2',
	});
	jupiterV2Clients.set(connection, v2Client);

	return v2Client;
};

export const getSwapToastId = (
	inMarketIndex: number,
	outMarketIndex: number
) => {
	return `swap-${inMarketIndex}-${outMarketIndex}`;
};

/**
 * Quote endpoints answer failures with a 4xx and an `{ error, errorCode }` body
 * that still parses as JSON, so a truthiness check isn't enough. Forwarding one
 * of those to the swap endpoint fails deserialization there instead
 * ("missing field `inputMint`"), which hides the real cause.
 */
export const isValidQuote = (
	quote: UnifiedQuoteResponse | undefined | null
): quote is UnifiedQuoteResponse => {
	if (!quote || quote.error || quote.errorCode) {
		return false;
	}

	return Boolean(
		quote.inputMint && quote.outputMint && quote.inAmount && quote.outAmount
	);
};

export const calcSwapAmountAtMaxSlippage = (
	isExactIn: boolean,
	quoteDetails: UnifiedQuoteResponse,
	slippage: number,
	swapFromMarket: SpotMarketConfig,
	swapToMarket: SpotMarketConfig
): string => {
	const otherAmount = +(
		(isExactIn ? quoteDetails?.outAmount : quoteDetails?.inAmount) ?? '0'
	);
	const slippageToApply = isExactIn ? 1 - slippage / 100 : 1 + slippage / 100;

	const precision = isExactIn
		? swapToMarket.precision
		: swapFromMarket.precision;
	const precisionExp = isExactIn
		? swapToMarket.precisionExp
		: swapFromMarket.precisionExp;

	const otherAmountAtMaxSlippage = (
		(otherAmount * slippageToApply) /
		(precision.toNumber() ?? 1)
	).toFixed(precisionExp.toNumber() ?? 0);

	return otherAmountAtMaxSlippage;
};

const TEN = new BN(10);

/**
 * The scale the pair price is read at, and the largest `parseInput` accepts.
 *
 * What survives is set by the price's decimal exponent, not by how many digits
 * a double holds: a value at or above about 1e-13 has its last significant
 * digit inside 30 fraction digits and parses exactly, and one below that loses
 * the tail (`3.3333333333333332e-15` does). The smallest ratio the listed
 * markets can produce is around 1e-5, a USDT amount priced in wBTC, so no live
 * pair comes near the edge.
 */
const PAIR_PRICE_SCALE = 30;

/** The 0.1% headroom the estimate carries, as an exact ratio. */
const BUFFER_NUMERATOR = new BN(1001);
const BUFFER_DENOMINATOR = new BN(1000);

export type ExactInSwapEstimate = {
	swapToAmount: string;
	/**
	 * The from token's price in to tokens, never the inverted one
	 * `useJupiterTokenPairData` returns for display on a stablecoin-from pair.
	 */
	tokenPairPrice: number;
	toPrecisionExp: number;
	fromPrecisionExp: number;
};

/**
 * The "from" amount an exact-out swap needs at the current pair price, with the
 * 0.1% headroom.
 *
 * This is written into the exact side of the swap form, so it is the amount
 * that gets submitted. Every step is fixed point: the typed "to" amount and the
 * price parse exactly, the dividend is shifted up by both the price scale and
 * the output precision so the one integer division lands on the from market's
 * last digit, and the result renders as a plain ungrouped decimal that already
 * fits that market. Nothing downstream has to reshape it, and it is never in
 * exponent form, on any path.
 *
 * The price reaches here as a float, so it is read through its decimal string.
 * A price that carries no positive fixed-point value at `PAIR_PRICE_SCALE` is
 * zero: an infinity, which is what dividing by it works out to anyway, and also
 * a NaN, a negative price, and one below 1e-30. The caller filters all four
 * with a truthiness check before it gets here.
 */
export const estimateExactInSwapFromAmount = ({
	swapToAmount,
	tokenPairPrice,
	toPrecisionExp,
	fromPrecisionExp,
}: ExactInSwapEstimate): string => {
	const price = parseInputBigNumOrZero(
		String(tokenPairPrice),
		PAIR_PRICE_SCALE
	).val;

	if (!price.gtn(0)) {
		return '0';
	}

	const toAmount = parseInputBigNumOrZero(swapToAmount, toPrecisionExp).val;

	const dividend = toAmount
		.mul(TEN.pow(new BN(PAIR_PRICE_SCALE + fromPrecisionExp)))
		.mul(BUFFER_NUMERATOR);
	const divisor = TEN.pow(new BN(toPrecisionExp))
		.mul(price)
		.mul(BUFFER_DENOMINATOR);

	return formatText(
		{ raw: dividend.div(divisor), scale: fromPrecisionExp },
		{
			digits: {
				kind: 'decimals',
				decimals: fromPrecisionExp,
				rounding: 'truncate',
			},
			grouping: false,
			trimTrailingZeros: true,
		}
	);
};

/**
 * What the "try exact in" button writes, or null when there is nothing worth
 * writing.
 *
 * An estimate below one base unit of the from market truncates to zero, and
 * writing a zero clears the field and blocks the swap with nothing said, so the
 * field is left as the user had it. Saying why instead is a follow-up.
 */
export const exactInSwapFromAmountToWrite = (
	estimate: ExactInSwapEstimate
): string | null => {
	const amount = estimateExactInSwapFromAmount(estimate);

	return amount === '0' ? null : amount;
};

export const getSimulatedQuoteValues = (
	swapAmount: BigNum,
	swapType: SwapMode,
	inputAssetOraclePrice: BigNum,
	outputAssetOraclePrice: BigNum
): {
	swapInAmount: BigNum;
	swapOutAmount: BigNum;
} => {
	const swapOutAmount =
		swapType === 'ExactOut'
			? swapAmount
			: swapAmount.scale(
					inputAssetOraclePrice.toNum() * 10_000,
					outputAssetOraclePrice.toNum() * 10_000
				);

	const swapInAmount =
		swapType === 'ExactIn'
			? swapAmount
			: swapAmount.scale(
					outputAssetOraclePrice.toNum() * 10_000,
					inputAssetOraclePrice.toNum() * 10_000
				);

	return { swapInAmount, swapOutAmount };
};
